import React, { useState, useEffect, useRef } from 'react'
import styles from './MoviePlayerModal.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlay,
  faPause,
  faVolumeUp,
  faVolumeMute,
  faExpand,
  faCompress,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faSpinner,
  faForward,
  faBackward,
  faFastForward,
  faFastBackward
} from '@fortawesome/free-solid-svg-icons'
import ImageWithFallback from '../ImageWithFallback/ImageWithFallback'

const MoviePlayerModal = ({ movie, episodes, initialEpisode, onClose, onEpisodeChange, asPage = false }) => {
  const videoRef = useRef(null)
  const containerRef = useRef(null)
  const seekBarRef = useRef(null)
  const isScrubbing = useRef(false)
  
  const [currentEpisode, setCurrentEpisode] = useState(initialEpisode || episodes[0] || null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [videoLoading, setVideoLoading] = useState(true)
  const [showControls, setShowControls] = useState(true)
  const [mouseIdleTimer, setMouseIdleTimer] = useState(null)

  const isMovie = movie?.type === 'movie' || !episodes || episodes.length === 0
  const isSeries = movie?.type === 'series' && episodes && episodes.length > 0

  useEffect(() => {
    if (!episodes || episodes.length === 0) {
      setCurrentEpisode(initialEpisode || null)
      return
    }

    if (initialEpisode) {
      const matchedEpisode = episodes.find((episode) => {
        if (!episode || !initialEpisode) return false
        if (episode.id && initialEpisode.id) return episode.id === initialEpisode.id
        if (episode.episode_number && initialEpisode.episode_number) {
          return episode.episode_number === initialEpisode.episode_number
        }
        return false
      })

      setCurrentEpisode(matchedEpisode || initialEpisode || episodes[0])
      return
    }

    setCurrentEpisode((prevEpisode) => prevEpisode || episodes[0])
  }, [episodes, initialEpisode])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleLoadStart = () => setVideoLoading(true)
    const handleCanPlay = () => setVideoLoading(false)
    const handleTimeUpdate = () => {
      if (!isScrubbing.current) {
        setCurrentTime(video.currentTime)
        if (seekBarRef.current) {
          seekBarRef.current.value = video.currentTime
        }
      }
    }
    const handleDurationChange = () => {
      setDuration(video.duration)
      if (seekBarRef.current && Number.isFinite(video.duration)) {
        seekBarRef.current.max = video.duration
      }
    }
    const handleVolumeChange = () => {
      setVolume(video.volume)
      setIsMuted(video.muted)
    }
    const handleSeeking = () => setVideoLoading(true)
    const handleSeeked = () => setVideoLoading(false)

    video.addEventListener('loadstart', handleLoadStart)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('durationchange', handleDurationChange)
    video.addEventListener('volumechange', handleVolumeChange)
    video.addEventListener('seeking', handleSeeking)
    video.addEventListener('seeked', handleSeeked)

    return () => {
      video.removeEventListener('loadstart', handleLoadStart)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('durationchange', handleDurationChange)
      video.removeEventListener('volumechange', handleVolumeChange)
      video.removeEventListener('seeking', handleSeeking)
      video.removeEventListener('seeked', handleSeeked)
    }
  }, [currentEpisode])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return

      switch(e.key) {
        case ' ':
        case 'k':
          e.preventDefault()
          togglePlayPause()
          break
        case 'ArrowLeft':
          e.preventDefault()
          skipBackward(10)
          break
        case 'ArrowRight':
          e.preventDefault()
          skipForward(10)
          break
        case 'j':
          e.preventDefault()
          skipBackward(10)
          break
        case 'l':
          e.preventDefault()
          skipForward(10)
          break
        case 'f':
          e.preventDefault()
          toggleFullscreen()
          break
        case 'm':
          e.preventDefault()
          toggleMute()
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [isPlaying, currentTime, duration])

  const togglePlayPause = () => {
    const video = videoRef.current
    if (video.paused) {
      video.play()
      setIsPlaying(true)
    } else {
      video.pause()
      setIsPlaying(false)
    }
  }

  const handleVolumeChange = (newVolume) => {
    const video = videoRef.current
    video.volume = newVolume
    setVolume(newVolume)
    if (newVolume === 0) {
      video.muted = true
    } else if (video.muted) {
      video.muted = false
    }
  }

  const toggleMute = () => {
    const video = videoRef.current
    video.muted = !video.muted
  }

  const seekToTime = (time) => {
    const video = videoRef.current
    if (!video) return
    const dur = video.duration
    if (!dur || !Number.isFinite(dur)) return
    const newTime = Math.max(0, Math.min(time, dur))
    video.currentTime = newTime
    setCurrentTime(newTime)
    if (seekBarRef.current) {
      seekBarRef.current.value = newTime
    }
  }

  const skipForward = (seconds) => {
    const video = videoRef.current
    if (video && Number.isFinite(video.currentTime)) {
      seekToTime(video.currentTime + seconds)
    }
  }

  const skipBackward = (seconds) => {
    const video = videoRef.current
    if (video && Number.isFinite(video.currentTime)) {
      seekToTime(video.currentTime - seconds)
    }
  }

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await containerRef.current.requestFullscreen()
    } else {
      await document.exitFullscreen()
    }
  }

  const handleEpisodeSelect = (episode) => {
    setCurrentEpisode(episode)
    setCurrentTime(0)
    if (onEpisodeChange) {
      onEpisodeChange(episode)
    }
    if (videoRef.current) {
      videoRef.current.currentTime = 0
      videoRef.current.play()
      setIsPlaying(true)
    }
  }

  const handleNextEpisode = () => {
    if (!isSeries) return
    const currentIndex = episodes.findIndex(ep => ep.id === currentEpisode.id)
    if (currentIndex < episodes.length - 1) {
      handleEpisodeSelect(episodes[currentIndex + 1])
    }
  }

  const handlePrevEpisode = () => {
    if (!isSeries) return
    const currentIndex = episodes.findIndex(ep => ep.id === currentEpisode.id)
    if (currentIndex > 0) {
      handleEpisodeSelect(episodes[currentIndex - 1])
    }
  }

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const handleMouseMove = () => {
    setShowControls(true)
    if (mouseIdleTimer) clearTimeout(mouseIdleTimer)
    const timer = setTimeout(() => {
      if (isPlaying && !isFullscreen) setShowControls(false)
    }, 3000)
    setMouseIdleTimer(timer)
  }

  const getVideoSource = () => {
    let source = null
    // Видео всегда хранится в Episode, MovieTitle полей video_file/video_url не имеет.
    // Для фильма берём первый (единственный) эпизод, для сериала — текущий.
    if (currentEpisode) {
      source = currentEpisode.video_file || currentEpisode.video_url || null
    } else if (episodes && episodes.length > 0) {
      source = episodes[0].video_file || episodes[0].video_url || null
    } else if (movie) {
      // запасной вариант, если вдруг поля появятся на MovieTitle
      source = movie.video_file || movie.video_url || null
    }

    if (source && source.startsWith('/media/')) {
      return `https://security.ncore.kg${source}`
    }
    return source || null
  }

  const videoSource = getVideoSource()
  const currentIndex = isSeries && currentEpisode ? episodes.findIndex(ep => ep.id === currentEpisode.id) : -1

  // Группировка эпизодов по сезонам
  const episodesBySeason = isSeries ? episodes.reduce((acc, episode) => {
    const season = episode.season_number || 1
    if (!acc[season]) acc[season] = []
    acc[season].push(episode)
    return acc
  }, {}) : {}

  return (
    <div className={asPage ? styles.pageWrapper : styles.modalOverlay} onClick={asPage ? undefined : onClose}>
      <div 
        className={`${styles.modalContent} ${isFullscreen ? styles.fullscreen : ''} ${asPage ? styles.pageContent : ''}`}
        onClick={(e) => e.stopPropagation()}
        ref={containerRef}
      >
        <button className={styles.closeButton} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className={styles.playerSection} onMouseMove={handleMouseMove}>
          {videoSource ? (
            <>
              <video
                ref={videoRef}
                className={styles.videoPlayer}
                src={videoSource}
                poster={currentEpisode?.thumbnail || movie?.poster}
                onClick={togglePlayPause}
                preload="auto"
              />

              {videoLoading && (
                <div className={styles.videoLoading}>
                  <FontAwesomeIcon icon={faSpinner} className={styles.spinner} spin />
                </div>
              )}

              {!isPlaying && !videoLoading && (
                <div className={styles.centerPlayButton} onClick={togglePlayPause}>
                  <FontAwesomeIcon icon={faPlay} />
                </div>
              )}

              <div className={`${styles.controls} ${showControls ? styles.visible : styles.hidden}`}>
                <div className={styles.progressContainer}>
                  <input
                    type="range"
                    className={styles.seekBar}
                    ref={seekBarRef}
                    defaultValue={0}
                    min={0}
                    max={duration || 100}
                    step="any"
                    onInput={(e) => {
                      const time = parseFloat(e.target.value)
                      const video = videoRef.current
                      if (video && Number.isFinite(video.duration)) {
                        video.currentTime = time
                        setCurrentTime(time)
                      }
                    }}
                    onPointerDown={() => { isScrubbing.current = true }}
                    onPointerUp={() => { isScrubbing.current = false }}
                  />
                </div>

                <div className={styles.controlsBar}>
                  <div className={styles.controlsLeft}>
                    <button className={styles.controlButton} onClick={togglePlayPause}>
                      <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                    </button>

                    {isSeries && (
                      <>
                        <button 
                          className={styles.controlButton}
                          onClick={handlePrevEpisode}
                          disabled={currentIndex === 0}
                          title="Предыдущий эпизод"
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>

                        <button 
                          className={styles.controlButton}
                          onClick={handleNextEpisode}
                          disabled={currentIndex === episodes.length - 1}
                          title="Следующий эпизод"
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </>
                    )}

                    <button 
                      className={styles.controlButton}
                      onClick={() => skipBackward(30)}
                      title="Назад 30 сек"
                    >
                      <FontAwesomeIcon icon={faFastBackward} />
                    </button>

                    <button 
                      className={styles.controlButton}
                      onClick={() => skipBackward(10)}
                      title="Назад 10 сек"
                    >
                      <FontAwesomeIcon icon={faBackward} />
                    </button>

                    <button 
                      className={styles.controlButton}
                      onClick={() => skipForward(10)}
                      title="Вперед 10 сек"
                    >
                      <FontAwesomeIcon icon={faForward} />
                    </button>

                    <button 
                      className={styles.controlButton}
                      onClick={() => skipForward(30)}
                      title="Вперед 30 сек"
                    >
                      <FontAwesomeIcon icon={faFastForward} />
                    </button>

                    <div className={styles.volumeControl}>
                      <button className={styles.controlButton} onClick={toggleMute}>
                        <FontAwesomeIcon icon={isMuted || volume === 0 ? faVolumeMute : faVolumeUp} />
                      </button>
                      
                      <div className={styles.volumeSlider}>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={isMuted ? 0 : volume}
                          onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                        />
                      </div>
                    </div>

                    <div className={styles.timeDisplay}>
                      <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
                    </div>
                  </div>

                  <div className={styles.controlsRight}>
                    <button className={styles.controlButton} onClick={toggleFullscreen}>
                      <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className={styles.noVideo}>
              <div className={styles.noVideoIcon}>🎬</div>
              <h3>Видео недоступно</h3>
              <p>{isMovie ? 'Для этого фильма пока нет видео' : 'Для этого эпизода пока нет видео'}</p>
            </div>
          )}
        </div>

        {!isFullscreen && (
          <>
            <div className={styles.movieInfoSection}>
              <div className={styles.movieInfoContainer}>
                {movie?.poster && (
                  <div className={styles.moviePoster}>
                    <ImageWithFallback
                      src={movie.poster}
                      alt={movie?.name || 'Poster'}
                      fallbackIcon="🎬"
                    />
                  </div>
                )}
                <div className={styles.movieDetails}>
                  <h2 className={styles.movieTitle}>{movie?.name || 'Фильм/Сериал'}</h2>
                  
                  <div className={styles.movieMetadata}>
                    {movie?.rating_avg && (
                      <div className={styles.metadataItem}>
                        <span className={styles.metadataLabel}>Рейтинг:</span>
                        <span className={styles.metadataValue}>⭐ {movie.rating_avg}/5</span>
                      </div>
                    )}
                    {isSeries && episodes.length > 0 && (
                      <div className={styles.metadataItem}>
                        <span className={styles.metadataLabel}>Эпизодов:</span>
                        <span className={styles.metadataValue}>{episodes.length}</span>
                      </div>
                    )}
                    {movie?.year && (
                      <div className={styles.metadataItem}>
                        <span className={styles.metadataLabel}>Год:</span>
                        <span className={styles.metadataValue}>{movie.year}</span>
                      </div>
                    )}
                    {movie?.status && (
                      <div className={styles.metadataItem}>
                        <span className={styles.metadataLabel}>Статус:</span>
                        <span className={styles.metadataValue}>{movie.status}</span>
                      </div>
                    )}
                  </div>

                  {movie?.description && (
                    <div className={styles.movieDescription}>
                      <p>{movie.description}</p>
                    </div>
                  )}

                  {movie?.genres && Array.isArray(movie.genres) && movie.genres.length > 0 && (
                    <div className={styles.movieGenres}>
                      {movie.genres.map((genre, idx) => (
                        <span key={idx} className={styles.genreTag}>
                          {typeof genre === 'string' ? genre : genre.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {isSeries && (
            <div className={styles.episodesSection}>
            <div className={styles.episodesHeader}>
              <h3>{movie?.name || 'Эпизоды'}</h3>
              <span className={styles.episodeCount}>{episodes.length} эпизодов</span>
            </div>

            <div className={styles.episodesList}>
              {Object.keys(episodesBySeason).sort((a, b) => a - b).map(seasonNum => (
                <div key={seasonNum} className={styles.seasonGroup}>
                  <h4 className={styles.seasonTitle}>Сезон {seasonNum}</h4>
                  <div className={styles.seasonEpisodes}>
                    {episodesBySeason[seasonNum].map((episode) => (
                      <div
                        key={episode.id}
                        className={`${styles.episodeItem} ${
                          currentEpisode?.id === episode.id ? styles.active : ''
                        }`}
                        onClick={() => handleEpisodeSelect(episode)}
                      >
                        <div className={styles.episodeNumber}>
                          {episode.episode_number}
                        </div>
                        
                        {episode.thumbnail && (
                          <div className={styles.episodeThumbnail}>
                            <ImageWithFallback
                              src={episode.thumbnail}
                              alt={`Эпизод ${episode.episode_number}`}
                              fallbackIcon="🎬"
                            />
                          </div>
                        )}
                        
                        <div className={styles.episodeInfo}>
                          <div className={styles.episodeName}>
                            {episode.name || `Эпизод ${episode.episode_number}`}
                          </div>
                          {episode.duration_min && (
                            <div className={styles.episodeDuration}>
                              ⏱ {episode.duration_min} мин
                            </div>
                          )}
                        </div>

                        {currentEpisode?.id === episode.id && (
                          <div className={styles.playingIndicator}>▶</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default MoviePlayerModal
